import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { MessageService } from "primeng/api";
import { forkJoin, take } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class NotificationService {
    private translocoService = inject(TranslocoService);
    private msgService = inject(MessageService);

    private showMessage(severity: "success" | "error" | "warn" | "info", messageKey: string, sticky: boolean = false) {
        forkJoin([
            this.translocoService.selectTranslate("general.messages.attention").pipe(take(1)),
            this.translocoService.selectTranslate(messageKey).pipe(take(1))
        ]).subscribe(([summary, detail]) => {
            this.msgService.add({
                key: "default",
                severity: severity,
                summary: summary,
                detail: detail,
                sticky: sticky
            });
        });
    }

    success(message: string, sticky: boolean = false): void {
        this.showMessage("success", message, sticky);
    }

    error(message: string, sticky: boolean = true): void {
        this.showMessage("error", message, sticky);
    }

    warning(message: string, sticky: boolean = false): void {
        this.showMessage("warn", message, sticky);
    }

    info(message: string, sticky: boolean = false): void {
        this.showMessage("info", message, sticky);
    }

    serviceError(response: Response | any) {
        if (response.status === 404) {
            let errorsString = "";
            if (response.error && response.error.errors) {
                for (const key in response.error.errors) {
                    const errorMessages = response.error.errors[key];

                    errorsString += errorMessages + "  ";
                }
            } else errorsString = "";

            if (errorsString.endsWith(", ")) errorsString = errorsString.slice(0, -1);

            if (errorsString.length > 0) {
                this.translocoService
                    .selectTranslate("general.messages.attention")
                    .pipe(take(1))
                    .subscribe((summary) => {
                        this.msgService.add({
                            key: "default",
                            severity: "error",
                            summary: summary,
                            detail: errorsString,
                            sticky: true,
                            life: 8000
                        });
                    });
            } else {
                this.info("general.messages.error404");
            }

            return;
        }
        if (response.status === 500) {
            this.error("general.messages.error500");

            return;
        }
        if (response.status === 504) {
            this.info("general.messages.error504");

            return;
        }

        if (response.statusText === "Unknown Error") {
            this.error("general.messages.unknowError");

            return;
        }

        this.error("general.messages.unknowError");

        return;
    }

    fluentError(resultFluent: Response | any) {
        if (resultFluent.isFailed) {
            if (resultFluent.errors.length > 0) {
                const errorMessages = resultFluent.errors.map((error) => error.reasons);
                let errorsString = resultFluent.errorMessages.join(", ");

                if (errorsString.endsWith(", ")) errorsString = errorsString.slice(0, -1);

                this.translocoService
                    .selectTranslate("general.messages.attention")
                    .pipe(take(1))
                    .subscribe((summary) => {
                        this.msgService.add({
                            key: "default",
                            severity: "error",
                            summary: summary,
                            detail: errorsString,
                            sticky: true
                        });
                    });
            }

            if (resultFluent.reasons.length > 0) {
                const reasonMessages = resultFluent.reasons.map((error) => error.message);
                let reasonsString = resultFluent.reasonMessages.join(", ");

                if (reasonsString.endsWith(", ")) reasonsString = reasonsString.slice(0, -1);

                this.translocoService
                    .selectTranslate("general.messages.attention")
                    .pipe(take(1))
                    .subscribe((summary) => {
                        this.msgService.add({
                            key: "default",
                            severity: "error",
                            summary: summary,
                            detail: reasonsString,
                            sticky: true
                        });
                    });
            }
        }
    }
}
